/**
 * Money on the client (SKPEOPLE-CC-001 rev1 CR-1).
 *
 * The API sends every amount as a MINOR-UNIT STRING — "7820000" for ₹78,200.00
 * — with a `currencyCode` beside it. Two consequences that matter here:
 *
 *   * NEVER `Number(...)` a money field and do arithmetic on it. A JSON number
 *     cannot hold an exact integer past 2^53, and the whole point of the string
 *     is that the client does not silently re-introduce the float drift the
 *     server was rewritten to remove. Sum with `sumMinor`, which stays in
 *     integers.
 *   * Formatting happens once, at the edge, in `formatMinor`.
 */

/** Parse a minor-unit wire string to an integer. Tolerates null/undefined. */
export function toMinor(wire: string | number | null | undefined): number {
  if (wire == null) return 0;
  const n = typeof wire === 'number' ? wire : parseInt(wire, 10);
  return Number.isFinite(n) ? n : 0;
}

/** Sum minor-unit wire values, staying in integers throughout. */
export function sumMinor(values: Array<string | number | null | undefined>): number {
  return values.reduce<number>((acc, v) => acc + toMinor(v), 0);
}

/**
 * Minor units -> a display string with grouping and a currency symbol.
 *
 * The division by 100 happens exactly once, here, on a value that is about to
 * become text and never be computed with again.
 */
export function formatMinor(
  minor: string | number | null | undefined,
  currency = 'INR',
  opts: { maximumFractionDigits?: number } = {},
): string {
  const value = toMinor(minor) / 100;
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency,
    maximumFractionDigits: opts.maximumFractionDigits ?? 2,
    minimumFractionDigits: opts.maximumFractionDigits === 0 ? 0 : 2,
  }).format(value);
}

/** Compact form for dashboard tiles (₹1.2L, ₹3.4Cr) — display only. */
export function formatMinorCompact(minor: string | number | null | undefined, currency = 'INR'): string {
  const value = toMinor(minor) / 100;
  if (Math.abs(value) >= 1_00_00_000) return `${currency === 'INR' ? '₹' : ''}${(value / 1_00_00_000).toFixed(1)}Cr`;
  if (Math.abs(value) >= 1_00_000) return `${currency === 'INR' ? '₹' : ''}${(value / 1_00_000).toFixed(1)}L`;
  return formatMinor(minor, currency, { maximumFractionDigits: 0 });
}

/** Major-unit user input (an HR-typed 50000.50) -> minor units for the wire. */
export function majorInputToMinor(input: string | number): number {
  const n = typeof input === 'number' ? input : parseFloat(input);
  if (!Number.isFinite(n)) return 0;
  // Rounded, not truncated: 50000.50 * 100 is 5000049.999… in binary float.
  return Math.round(n * 100);
}
